import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { ProjectService, ToolService } from "../../services";
import { moveToolValidationRules } from "../../pages/project/validation";

export const MoveTool = ({ open = false, projectStoreId, onClose = () => {}, onSuccess = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [companyDetails, setCompanyDetails] = useState({});
  const [toolQuantity, setToolQuantity] = useState(0);
  const [toolDetails, setToolDetails] = useState({});
  const [storeIds, setStoreIds] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [toolMovementDetails, setToolMovementDetails] = useState({
    fromStoreId: undefined,
    toStoreId: undefined,
    movedQuantity: undefined,
    movedById: undefined,
  });

  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (open && user) setCompanyDetails(user.company);
  }, [open, user]);

  const fetchData = async (toolId) => {
    setLoading(true);
    try {
      const { data: tool } = await ToolService.getToolById(toolId);
      const stores = Array.from(new Set(tool?.toolInventory?.map((item) => item.storeId)));
      setStoreIds(stores);
      setToolDetails(tool);
    } catch (error) {
      toast.error("Failed to fetch assigned tools");
      console.error("Failed to fetch assigned tools");
    } finally {
      setLoading(false);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setToolMovementDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "movedQuantity") {
      const num = parseFloat(value);

      if (value !== "" && (isNaN(num) || num <= 0)) setErrors((prev) => ({ ...prev, movedQuantity: "must be greater than 0" }));
      else if (value !== "" && !Number.isInteger(num))
        setErrors((prev) => ({ ...prev, movedQuantity: "decimal values not allowed" }));
      else if (selectedTool !== null && selectedInventory !== null && num > toolQuantity)
        setErrors((prev) => ({ ...prev, movedQuantity: `only (${toolQuantity}) quantities available` }));
      else
        setErrors((prev) => {
          const { movedQuantity, ...rest } = prev;
          return rest;
        });
    }
  };

  const handleToolChange = (selected) => {
    setSelectedTool(selected);
    setSelectedInventory(null);
    setToolQuantity(0);
    setErrors((prev) => ({ ...prev, movedQuantity: "" }));

    if (selected) fetchData(selected?.id);
  };

  const handleInventoryChange = (selected) => {
    setSelectedInventory(selected);
    if (selected === null) setErrors((prev) => ({ ...prev, movedQuantity: "" }));
    else {
      setToolMovementDetails((prev) => ({
        ...prev,
        fromStoreId: selected?.storeId || null,
      }));

      const quantity = getToolQuantity(toolDetails?.toolInventory, selected?.storeId);
      setToolQuantity(quantity);

      if (toolMovementDetails?.movedQuantity) {
        const num = parseFloat(toolMovementDetails.movedQuantity);
        if (num > quantity) {
          setErrors((prev) => ({
            ...prev,
            movedQuantity: `only (${quantity}) quantities available`,
          }));
        } else {
          setErrors((prev) => {
            const { movedQuantity, ...rest } = prev;
            return rest;
          });
        }
      }
    }
  };

  const handleClearForm = () => {
    setToolMovementDetails({
      fromStoreId: undefined,
      toStoreId: undefined,
      movedQuantity: undefined,
      movedById: undefined,
    });
    setSelectedInventory(null);
    setSelectedTool(null);
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const getToolQuantity = (toolInventory = [], storeId) => {
    const inventory = toolInventory.find((item) => item.storeId === storeId);
    return inventory ? inventory.quantity : 0;
  };

  const getFilteredProjectOptions = (options = [], projectStoreId, toolInventory = [], companyDetails) => {
    // get all unique storeIds from toolInventory with quantity > 0
    const inventoryStoreIds = new Set(toolInventory.filter((item) => item.quantity > 0).map((item) => item.storeId));

    // filter options that match toolInventory storeIds and are not the current project store
    const matchingOptions = options.filter((opt) => inventoryStoreIds.has(opt.storeId) && opt.storeId !== projectStoreId);

    // create the company warehouse option
    const companyOption = {
      id: "company-warehouse",
      name: "Company Warehouse",
      storeId: companyDetails?.storeId,
    };

    // return company warehouse at the top + matching store options
    return [companyOption, ...matchingOptions];
  };

  const handleMoveTool = async () => {
    setErrors({});

    const validationErrors = moveToolValidationRules({
      ...toolMovementDetails,
      toolId: selectedTool?.id,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // validate quantity
    if (toolMovementDetails.movedQuantity > toolQuantity) {
      setErrors((prev) => ({ ...prev, movedQuantity: `only (${toolQuantity}) quantities available` }));
      return;
    }

    if (toolMovementDetails.movedQuantity) toolMovementDetails.movedQuantity = Number(toolMovementDetails.movedQuantity);
    const payload = {
      ...toolMovementDetails,
      toStoreId: projectStoreId,
      movedById: user?.id,
    };

    setLoading(true);
    try {
      await ToolService.assignTool(selectedTool?.id, payload);
      toast.success("Tool moved successfully.");
      onSuccess?.();
    } catch (error) {
      console.error("Error moving tool:", error);
      toast.error("Failed to move tool.");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };
  return (
    <FormDialog
      open={open}
      title={"Move Tool "}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleMoveTool} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <Box sx={{ width: 250 }}>
            <SelectField
              label="Tool"
              value={selectedTool}
              getData={ToolService.getTools}
              required
              error={!!errors.toolId}
              helperText={errors.toolId}
              onChange={handleToolChange}
            />
          </Box>
          <Box sx={{ flex: 1, width: 250 }}>
            <InputField
              required
              type="number"
              label="Quantity"
              name="movedQuantity"
              value={toolMovementDetails?.movedQuantity}
              onChange={updateDetailsState}
              error={!!errors.movedQuantity}
              helperText={errors.movedQuantity}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Move From"
            value={selectedInventory}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{
              limit: toolDetails?.toolInventory?.length,
              storeIds: storeIds.join(","),
              includeDeleted: true,
            }}
            disabled={!selectedTool}
            required
            error={!!errors.fromStoreId}
            helperText={errors.fromStoreId}
            onChange={handleInventoryChange}
            optionFilter={(options) =>
              getFilteredProjectOptions(options, projectStoreId, toolDetails?.toolInventory, companyDetails)
            }
            style={{ width: "100%" }}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
